// Sea-facing land edges ("shore edges"): the single list that the ramp mesh, the shore field texture, the
// beach/cliff shading and the rock placement all read. Pure (no THREE), deterministic: sorted by (r, q, edge).

import type { SceneNode } from '@aod/protocol';
import {
  LAND_TOP_LIFT,
  TILE_PLATEAU,
  TILE_RADIUS,
  tileSurfaceY,
  waterSurfaceY,
} from '../elevation.js';
import { EDGE_NEIGHBOURS, WATER_BIOMES, edgeNormal } from '../terrainEdges.js';
import { type CoastKind, coastKindFor } from './shoreKind.js';

const SEA: ReadonlySet<string> = new Set(['ocean', 'coast']);

export interface ShoreEdge {
  readonly q: number;
  readonly r: number;
  /** Edge index 0..5 (terrainEdges: between hex corners i and i+1). */
  readonly i: number;
  readonly kind: CoastKind;
  /** Tile centre (world XZ). */
  readonly cx: number;
  readonly cz: number;
  /** The two lattice corners (circumradius 1 hex), A = corner i, B = corner i + 1. */
  readonly ax: number;
  readonly az: number;
  readonly bx: number;
  readonly bz: number;
  /** Outward normal (towards the sea). */
  readonly nx: number;
  readonly nz: number;
  /** tileSurfaceY of the land tile (its plateau edge). */
  readonly top: number;
  /** Y of the sea tile's water plane across this edge. */
  readonly waterY: number;
}

/** Corner k of a hex centred at (cx, cz): angle 30 + 60k degrees, circumradius 1. */
export function hexCorner(cx: number, cz: number, k: number): [number, number] {
  const a = ((30 + 60 * (((k % 6) + 6) % 6)) * Math.PI) / 180;
  return [cx + Math.cos(a), cz + Math.sin(a)];
}

/**
 * Every land-tile edge that faces open water (ocean or coast), except edges carrying a river (the river /
 * waterfall shading owns those). Rock edges follow shoreKind.coastKindFor. `visibleCoords` is not applied
 * here: the ramp mesh is built once per terrain and dimmed per tile, never rebuilt on fog changes.
 */
export function collectShoreEdges(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): ShoreEdge[] {
  const out: ShoreEdge[] = [];
  for (const tile of tiles) {
    const props = (tile.props ?? {}) as Record<string, unknown>;
    const q = props.q as number | undefined;
    const r = props.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const biome = (props.biome as string | undefined) ?? 'plains';
    if (WATER_BIOMES.has(biome)) continue;
    const riverEdges = Number(props.riverEdges ?? 0);
    const [cx, , cz] = tile.transform.position;
    let top: number | undefined;
    let kind: CoastKind | undefined;
    for (let i = 0; i < 6; i++) {
      const [dq, dr] = EDGE_NEIGHBOURS[i]!;
      const nb = biomeMap.get(`${q + dq},${r + dr}`);
      if (nb === undefined || !SEA.has(nb) || riverEdges & (1 << i)) continue;
      top ??= tileSurfaceY(q, r, biomeMap);
      kind ??= coastKindFor(biome, top - LAND_TOP_LIFT, q, r);
      const [ax, az] = hexCorner(cx, cz, i);
      const [bx, bz] = hexCorner(cx, cz, i + 1);
      const [nx, nz] = edgeNormal(i);
      out.push({
        q,
        r,
        i,
        kind,
        cx,
        cz,
        ax,
        az,
        bx,
        bz,
        nx,
        nz,
        top,
        waterY: waterSurfaceY(q + dq, r + dr, biomeMap),
      });
    }
  }
  out.sort((a, b) => a.r - b.r || a.q - b.q || a.i - b.i);
  return out;
}

/** The plateau edge sits this far inside the lattice hex edge (TILE_PLATEAU of the apothem). */
export const PLATEAU_INSET = (1 - TILE_PLATEAU) * Math.cos(Math.PI / 6);
/** The rim (bottom of the chamfer) sits this far inside the lattice edge. */
export const RIM_INSET = (1 - TILE_RADIUS) * Math.cos(Math.PI / 6);
